'use client'
import { useEffect, useMemo, useState } from 'react'
import Link from 'next/link'
import { usePathname } from 'next/navigation'
import clsx from 'clsx'
import { useTranslation } from 'react-i18next'
import { type HeaderMenuProps } from '@/blocks/HeaderBlock/HeaderBlock'
import { NestedMenuBlock } from '@/blocks/HeaderBlock/NestedMenuBlock'
import { useProductMetadata } from '@/components/LayoutWithMenu/productMetadataHooks'
import { useResponsiveVariant } from '@/utils/useResponsiveVariant'
import * as NavigationMenuPrimitive from '@radix-ui/react-navigation-menu'
import { Text } from 'ui/src/components/Text/Text'
import { CrossIcon } from 'ui/src/icons/CrossIcon'
import { visuallyHidden } from 'ui/src/utils/visibility.css'
import { HeaderMenuMobile } from '../HeaderMenuMobile/HeaderMenuMobile'
import { SeoNav } from '../SeoNav/SeoNav'
import { ShoppingCartMenuItem } from '../ShoppingCartMenuItem/ShoppingCartMenuItem'
import { headerMenuDesktop, navigationPrimaryList } from '../Header.css'
import { backLink, backWrapper, headerMenu, headerMenuMobile } from './HeaderMenu.css'

export type HeaderMenuDesktopProps = {
  items: HeaderMenuProps
  defaultValue?: string
}

export const HeaderMenu = ({ defaultValue, items }: HeaderMenuDesktopProps) => {
  const { t } = useTranslation()
  const [activeItem, setActiveItem] = useState('')
  const pathname = usePathname()
  const variant = useResponsiveVariant('lg')
  const productMetadata = useProductMetadata()

  const product = productMetadata?.find((product) => pathname === product.priceCalculatorPageLink)

  const content = useMemo(() => {
    // Optimization: we don't want to assume default variant to avoid rendering twice on miss
    if (variant === 'initial') return null
    return items.map((item) => <NestedMenuBlock key={item._uid} blok={item} variant={variant} />)
  }, [items, variant])

  const priceCalculatorMenu = useMemo(() => {
    // Optimization: we don't want to assume default variant to avoid rendering twice on miss
    if (variant === 'initial') return null

    return (
      <>
        {product && (
          <li>
            <Link
              href={product.pageLink}
              className={backLink}
              aria-label={t('GO_BACK_PRODUCT_PAGE_LINK_LABEL', { product: product.displayNameFull })}
            >
              <div className={backWrapper}>
                <CrossIcon aria-hidden="true" size="1.5rem" />
              </div>
              <Text className={clsx(variant === 'mobile' && visuallyHidden)}>{product.displayNameFull}</Text>
            </Link>
          </li>
        )}
      </>
    )
  }, [product, variant, t])

  useEffect(() => {
    setActiveItem('')
  }, [pathname])

  return (
    <>
      <div className={headerMenu}>
        {variant === 'desktop' && (
          <NavigationMenuPrimitive.Root className={headerMenuDesktop} value={activeItem} onValueChange={setActiveItem}>
            <NavigationMenuPrimitive.List className={navigationPrimaryList}>
              {priceCalculatorMenu}
              {content}
            </NavigationMenuPrimitive.List>
          </NavigationMenuPrimitive.Root>
        )}

        {variant === 'mobile' && (
          <>
            {product && <ul>{priceCalculatorMenu}</ul>}
            <div className={headerMenuMobile}>
              <HeaderMenuMobile defaultValue={defaultValue}>{content}</HeaderMenuMobile>
            </div>
          </>
        )}

        <ShoppingCartMenuItem />
      </div>

      {/* SEO-only nav visually hidden - always in HTML for crawlers */}
      <SeoNav items={items} />
    </>
  )
}
